import React from 'react'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  MenuItem,
  Typography,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useApi } from '@core/api'
import type { GetIntegrationOutput, IntegrationType } from '@core/api/generated/types.gen'
import { TextField, TextFieldProps } from '@core/components/textfield/textfield'
import { addSnack } from '@core/snack/snack-state'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type Integration = GetIntegrationOutput

const getDefaultValues = (integration?: GetIntegrationOutput): Integration => {
  return {
    accessKey: integration?.accessKey || '',
    secretKey: integration?.secretKey || '',
    subjectRef: integration?.subjectRef || '',
    centreRef: integration?.centreRef || '',
    subjectUrlId: integration?.subjectUrlId || '',
    // @ts-expect-error
    target: integration?.target || 'BILPAS',
    authMethod: integration?.authMethod || 'BASIC',
    adfsUrl: integration?.adfsUrl || '',
    adfsRealm: integration?.adfsRealm || '',
  }
}

const integrationKeys = {
  list: ['integrations', 'list'],
  upsert: ['integrations', 'upsert'],
  test: ['integrations', 'test'],
}

export const AdminManageIntegrations = () => {
  const { t } = useTranslation('legacy', { keyPrefix: 'admin.manage_integrations' })
  const form = useForm({ defaultValues: getDefaultValues() })
  const { register, formState, handleSubmit } = form
  const defaultTargetValue = React.useRef(form.getValues('target'))
  const defaultAuthMethodValue = React.useRef(form.getValues('authMethod'))
  const isAdfsAuthMethod = form.watch('authMethod') === 'ADFS'

  const [initializedWithPassword, setInitializedWithPassword] = React.useState(false)
  const api = useApi()
  const queryClient = useQueryClient()

  const integrationQuery = useQuery({
    queryKey: integrationKeys.list,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    queryFn: async () => {
      const { data } = await api.getIntegration({
        path: {
          integration_type: 'ProBuilder',
        },
        throwOnError: true,
      })

      if (!data || typeof data !== 'object') {
        return getDefaultValues()
      }

      const values = getDefaultValues(data)

      form.reset(values)
      defaultTargetValue.current = values.target
      defaultAuthMethodValue.current = values.authMethod

      setInitializedWithPassword(Boolean(values.secretKey))

      return values
    },
  })

  const upsertCustomer = useMutation({
    mutationKey: integrationKeys.upsert,
    mutationFn: async (variables: { integrationType: IntegrationType; data: Integration }) => {
      const { data } = await api.upsertIntegration({
        body: variables.data,
        path: {
          integration_type: variables.integrationType,
        },
        throwOnError: true,
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.list,
      })
    },
  })

  const testConnection = useMutation({
    mutationKey: integrationKeys.test,
    mutationFn: async () => {
      const { data } = await api.testIntegration({
        path: {
          integration_type: 'ProBuilder',
        },
        throwOnError: true,
      })

      return data
    },
  })

  const handleTestConnection = async () => {
    testConnection.mutate()
  }

  const getProps = (field: keyof Integration, maxLength = 128): TextFieldProps => {
    const error = formState.errors[field]
    let message = error?.message

    if (error?.type === 'maxLength') {
      message = t('max_length', { length: maxLength })
    } else if (error?.type === 'minLength') {
      message = t('min_length')
    }

    // Determine if field is required based on conditions
    let isRequired = true
    if (field === 'adfsUrl' || field === 'adfsRealm') {
      isRequired = isAdfsAuthMethod
    }

    const fieldProps = register(field, {
      maxLength,
      minLength: isRequired ? 1 : undefined,
      required: isRequired ? t('required') : false,
    })

    return {
      ...fieldProps,
      disabled: formState.isSubmitting || testConnection.isPending,
      error: Boolean(error),
      required: isRequired,
      helperText: message,
      autoComplete: 'off',
    }
  }

  const onSubmit = async (values: Integration) => {
    try {
      testConnection.reset()

      const response = await upsertCustomer.mutateAsync({
        integrationType: 'ProBuilder',
        data: values,
      })

      if (response?.accessKey) {
        addSnack({
          message: t('integration_updated'),
          severity: 'success',
        })

        return
      }

      console.error(response, 'AdminManageIntegrations.onSubmit response error')

      addSnack({
        message: t('something_went_wrong'),
        severity: 'warning',
      })
    } catch (error) {
      const firstError = error.detail?.[0]

      if (!firstError) {
        addSnack({ message: t('fatal_error'), severity: 'error' })
        return
      }

      form.setError(firstError.loc[2], { message: firstError.msg })

      console.error(error, 'AdminManageIntegrations.onSubmit error', firstError)
    }
  }

  if (integrationQuery.isLoading) {
    return <div>{t('loading')}</div>
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} name="probuilder-integration">
      <DialogContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('probuilder_integration')}
        </Typography>

        <TextField
          required
          {...getProps('target')}
          label={t('target')}
          aria-label="ProBuilder target"
          fullWidth
          select
          key={defaultTargetValue.current}
          defaultValue={defaultTargetValue.current}
        >
          <MenuItem value="BILPAS">BILPAS</MenuItem>
          <MenuItem value="PAS">PAS</MenuItem>
          <MenuItem value="PROD">{t('prod_us')}</MenuItem>
          <MenuItem value="PROD_EU">{t('prod_eu')}</MenuItem>
          <MenuItem value="PROD_JP">{t('prod_jp')}</MenuItem>
          <MenuItem value="PROD_CFA">{t('prod_cfa')}</MenuItem>
        </TextField>

        <Box my={2} />

        <TextField
          required
          {...getProps('authMethod')}
          label={t('auth_method')}
          aria-label="Auth Method"
          fullWidth
          select
          key={defaultAuthMethodValue.current}
          defaultValue={defaultAuthMethodValue.current}
        >
          <MenuItem value="BASIC">BASIC</MenuItem>
          <MenuItem value="ADFS">ADFS</MenuItem>
        </TextField>

        <Box my={2} />

        <TextField
          required
          {...getProps('accessKey')}
          label={t('username')}
          aria-label="ProBuilder username"
          fullWidth
        />

        <Box my={2} />

        <TextField
          required
          {...getProps('secretKey', 1024)}
          label={t('password')}
          type="password"
          aria-label="ProBuilder password"
          fullWidth
          disabled={initializedWithPassword}
        />

        {initializedWithPassword && (
          <Button
            size="small"
            sx={{ mt: 1 }}
            disabled={testConnection.isPending}
            onClick={() => {
              setInitializedWithPassword(false)
              form.setValue('secretKey', '')
              testConnection.reset()
              setTimeout(() => {
                form.setFocus('secretKey')
              }, 100)
            }}
          >
            {t('clear_password')}
          </Button>
        )}

        {isAdfsAuthMethod && (
          <>
            <Box my={2} />
            <TextField
              {...getProps('adfsUrl')}
              label={t('adfs_url')}
              aria-label="ProBuilder adfsUrl"
              fullWidth
            />
            <Box my={2} />
            <TextField
              {...getProps('adfsRealm')}
              label={t('adfs_realm')}
              aria-label="ProBuilder adfsRealm"
              fullWidth
            />
          </>
        )}

        <Divider sx={{ my: 4 }} />

        <TextField
          required
          {...getProps('centreRef')}
          label={t('centre_reference')}
          aria-label="ProBuilder centreRef"
          fullWidth
        />

        <Box mt={2} />
        <TextField
          required
          {...getProps('subjectRef')}
          label={t('subject_reference')}
          aria-label="ProBuilder subjectRef"
          fullWidth
        />

        <Box mt={2} />
        <TextField
          required
          {...getProps('subjectUrlId')}
          label={t('subject_url_id')}
          aria-label="ProBuilder subjectUrlId"
          helperText={t('subject_url_id_helper')}
          fullWidth
        />
      </DialogContent>

      <DialogActions>
        {initializedWithPassword && (
          <LoadingButton
            onClick={handleTestConnection}
            variant="contained"
            loading={testConnection.isPending}
            disabled={formState.isSubmitting || formState.isDirty}
          >
            {t('test_integration')}
          </LoadingButton>
        )}

        <LoadingButton
          variant="contained"
          disabled={formState.isSubmitting || !formState.isDirty}
          loading={formState.isSubmitting}
          type="submit"
        >
          {t('update')}
        </LoadingButton>
      </DialogActions>

      {testConnection.data && (
        <Box my={1} px={4}>
          <Divider sx={{ my: 4 }} />
          <TextField
            label={t('connection_result')}
            value={JSON.stringify(testConnection.data.result, null, 2)}
            maxRows={16}
            multiline
            onChange={() => {}}
            fullWidth
            spellCheck={false}
          />
        </Box>
      )}
    </Box>
  )
}
